import { useEffect, useMemo, useState } from 'react';
import { useAsync } from 'react-use';

import { ServicesGridBlockKind } from '@mssgme/shared';

const PRODUCT_KIND = 'product';
const SERVICE_KIND = 'service';
const getIdsByKind = (items, kind) => items.filter((item) => item.kind === kind).map(({ productId }) => productId);

export const useOrderProducts = (website, order, retrieveFn) => {
    const [resolved, setResolved] = useState(null);
    const { pages = [] } = website;
    const { value } = useAsync(async () => {
        const productIds = getIdsByKind(order.items, PRODUCT_KIND);
        const serviceIds = getIdsByKind(order.items, SERVICE_KIND);
        const products = [];
        const services = [];

        if (productIds.length && retrieveFn) {
            await Promise.all(
                productIds.map(async (productId) => {
                    try {
                        const product = await retrieveFn({ productId });

                        if (product) {
                            products.push(product);
                        }
                    } catch (e) {
                        // eslint-disable-next-line no-console
                        console.warn(`Product with ID "${productId}" not found`);
                    }
                })
            );
        }

        if (serviceIds.length) {
            let total = serviceIds.length;
            const missing = new Set(serviceIds);

            for (const page of pages) {
                for (const block of page.blocks) {
                    if (block.kind !== ServicesGridBlockKind) {
                        continue;
                    }

                    for (const item of block.items) {
                        if (missing.has(item._id)) {
                            services.push(item);
                            total--;

                            if (total <= 0) {
                                break;
                            }
                        }
                    }

                    if (total <= 0) {
                        break;
                    }
                }

                if (total <= 0) {
                    break;
                }
            }
        }

        return {
            products,
            services,
        };
    }, [order.items, retrieveFn]);

    useEffect(() => {
        if (value) {
            setResolved(value);
        }
    }, [value]);

    return useMemo(
        () =>
            order.items.map((item) => {
                const product =
                    resolved &&
                    (item.kind === PRODUCT_KIND ? resolved.products : resolved.services).find(
                        ({ _id }) => _id === item.productId
                    );

                return {
                    ...item,
                    product: product || { name: item.name, price: item.price, photoUrl: '' },
                };
            }),
        [order.items, resolved]
    );
};
